import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [done, setDone] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, d] = await Promise.all([
        api.get("/projects"),
        api.get("/projects/completions").catch(() => ({ data: [] })),
      ]);
      setProjects(p.data); setDone(d.data);
    })();
  }, []);

  const doneIds = new Set(done.map((x) => x.project_id));

  const complete = async (id) => {
    try {
      const { data } = await api.post(`/projects/${id}/complete`);
      if (data.already) toast("Already marked complete");
      else toast.success(`+${data.points_awarded} points`);
      setDone((prev) => [...prev, { project_id: id }]);
    } catch (e) { toast.error("Please log in as a student to complete projects"); }
  };

  return (
    <div className="space-y-8" data-testid="projects-page">
      <div>
        <div className="chip mb-2">Cross-subject</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Big projects that mix subjects</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">Choose one when you want a bigger win. Each project ties together two or three subjects.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="projects-grid">
        {projects.map((p) => (
          <div key={p.id} data-testid={`project-card-${p.id}`}
            className={`p-6 rounded-3xl border ${doneIds.has(p.id) ? "bg-teal-50 border-teal-200" : "bg-white border-slate-200"}`}>
            <div className="flex items-center justify-between">
              <div className="flex gap-1 flex-wrap">
                {p.subject_tags.map((t) => <span key={t} className="chip">{t}</span>)}
              </div>
              <span className="text-xs text-slate-500">~{p.estimated_min} min</span>
            </div>
            <div className="mt-3 font-display text-xl font-semibold text-slate-900">{p.title}</div>
            <p className="text-sm text-slate-600 mt-1">{p.goal}</p>
            <ol className="list-decimal ml-5 mt-3 space-y-1 text-sm text-slate-700">
              {p.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="mt-3 text-xs text-slate-500">Deliverable: {p.deliverable}</div>
            <div className="mt-4">
              {doneIds.has(p.id) ? (
                <div className="inline-flex items-center gap-1 text-teal-700 font-semibold text-sm"><CheckCircle2 size={14} /> Completed</div>
              ) : (
                <button data-testid={`project-complete-${p.id}`} onClick={() => complete(p.id)}
                  className="px-5 py-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold inline-flex items-center gap-1">
                  Mark complete <ArrowRight size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
